import React, {
  MouseEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { animated, SpringValue, useTransition } from "@react-spring/web"
import { DimensionContext } from "@contexts/Dimension"

interface MessageHubProperties {
  children: (add: AddFunction) => void
}

export interface MessageInfo {
  duration: number
  message: string
}

export type AddFunction = (info: MessageInfo) => void

interface Item extends MessageInfo {
  key: number
}

const ANIMATION_CONFIG = { friction: 20, precision: 0.1, tension: 125 }

interface CloseButtonProperties {
  onClick: (e: MouseEvent<HTMLButtonElement>) => void
  size: number
}

const CloseButton: React.FC<CloseButtonProperties> = ({ onClick, size }) => (
  <button
    className="shrink-0 bg-transparent"
    style={{ pointerEvents: "auto" }}
    onClick={onClick}
  >
    <svg
      style={{
        height: `${size * 0.25}px`,
        width: `${size * 0.25}px`,
      }}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path d="M6 18L18 6M6 6l12 12"></path>
    </svg>
  </button>
)

export function ToastHub({ children }: MessageHubProperties) {
  const { playerSize, safeArea } = useContext(DimensionContext)
  const [items, setItems] = useState<Item[]>([])
  const referenceMap = useMemo(() => new WeakMap<Item, HTMLDivElement>(), [])
  const cancelMap = useMemo(() => new WeakMap<Item, () => void>(), [])
  const { isDark } = useContext(DimensionContext)

  const transitions = useTransition(items, {
    config: (item: Item, index: number, phase: string) => (key: string) =>
      phase === "enter" && key === "life"
        ? { duration: item.duration }
        : ANIMATION_CONFIG,
    enter:
      (item: Item) =>
      async (next: (config: object) => Promise<void>, cancel: () => void) => {
        cancelMap.set(item, cancel)
        await next({
          height: referenceMap.get(item)?.offsetHeight || 0,
          opacity: 1,
        })
        await next({ life: "0%" })
      },
    from: { height: 0, life: "100%" as const, opacity: 0 },
    keys: (item: Item) => item.key,
    leave: [{ opacity: 0 }, { height: 0 }],
    onRest: (_result, _ctrl, item: Item) => {
      setItems((state) => state.filter((index) => index.key !== item.key))
    },
  })

  const addToast: AddFunction = ({ duration, message }) => {
    setItems((state) => {
      const newItems = [...state, { duration, key: Date.now(), message }]
      if (newItems.length > 2) {
        const [first, ...rest] = newItems
        cancelMap.get(first)?.()
        return rest
      }
      return newItems
    })
  }

  useEffect(() => {
    children(addToast)
  }, [children])

  return (
    <div
      className="pointer-events-none fixed left-3 right-3 top-10 z-[70] items-center gap-2"
      style={{ marginTop: safeArea.topSafeArea }}
    >
      {transitions((style, item) => (
        <animated.div
          className="relative mb-2 box-border w-full overflow-hidden"
          style={
            style as {
              height: SpringValue<number>
              opacity: SpringValue<number>
            }
          }
        >
          <div
            className="flex gap-2 overflow-hidden rounded-xl p-2"
            ref={(reference: HTMLDivElement | null) => {
              if (reference) referenceMap.set(item, reference)
            }}
            style={{
              backgroundColor: isDark ? "#403F3F" : "#E2E1E1",
              color: "#DA5151",
            }}
          >
            <p className="flex-1 text-center">{item.message}</p>
            <CloseButton
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                const life = style.life as SpringValue<string>
                if (cancelMap.has(item) && life.get() !== "0%")
                  cancelMap.get(item)?.()
              }}
              size={playerSize}
            />
          </div>
        </animated.div>
      ))}
    </div>
  )
}
